import { useCallback, useEffect, useState } from 'react';
import { CarouselApi } from './carousel.tsx';

type UseCarouselTriggerType = {
  selectedIndex: number;
  scrollSnaps: number[];
  handleTrigger: (index: number) => void;
  handleAutoplayTrigger: (callback: () => void) => void;
};

export const useCarouselTrigger = (
  emblaApi: CarouselApi | undefined,
  defaultIdx: number = 0,
): UseCarouselTriggerType => {
  const [selectedIndex, setSelectedIndex] = useState(defaultIdx);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const handleTrigger = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  const onInit = useCallback((emblaApi: CarouselApi) => {
    if (emblaApi) {
      setScrollSnaps(emblaApi.scrollSnapList());
    }
  }, []);

  const onSelect = useCallback((emblaApi: CarouselApi) => {
    if (emblaApi) {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    }
  }, []);

  const handleAutoplayTrigger = useCallback(
    (callback: () => void) => {
      const autoplay = emblaApi?.plugins()?.autoplay;
      if (!autoplay) return;

      const resetOrStop =
        autoplay.options.stopOnInteraction === false
          ? autoplay.reset
          : autoplay.stop;

      resetOrStop();
      callback();
    },
    [emblaApi],
  );

  useEffect(() => {
    if (!emblaApi) return;

    // eslint-disable-next-line react-hooks/set-state-in-effect
    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect);
  }, [emblaApi, onInit, onSelect]);

  return {
    selectedIndex,
    scrollSnaps,
    handleTrigger,
    handleAutoplayTrigger,
  };
};
